import React, { useEffect, useRef, type ReactNode } from 'react';

import useLiquidRenderer from '@hooks/shared/useLiquidRenderer';
import { Modal, Button, Spin, Alert } from 'antd';

import TemplateIframe from './TemplateIframe';

interface PaperSize {
  width: number;
  height: number;
}

interface TemplatePreviewModalProps {
  visible: boolean;
  onCancel: () => void;
  content?: string;
  sampleData?: Record<string, unknown>; // 🔥 外部輸入 data 的入口
  paperSize: PaperSize;
  cssVariablePrefix?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  namespace?: string;
}

/**
 * TemplatePreviewModal - Reusable modal for previewing Liquid templates
 *
 * @param props.visible - Whether modal is visible
 * @param props.onCancel - Close modal callback
 * @param props.content - Liquid template content
 * @param props.sampleData - Sample data for rendering (EXTERNAL INPUT - 外部輸入 data 的入口)
 * @param props.paperSize - Paper size { width, height } in mm
 * @param props.cssVariablePrefix - CSS variable prefix ('paper' or 'label')
 * @param props.t - i18n translate function
 * @param props.namespace - i18n namespace (e.g., 'shared.order_templates')
 */
const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  visible,
  onCancel,
  content,
  sampleData, // 🔥 外部輸入 data 的入口
  paperSize,
  cssVariablePrefix = 'paper',
  t,
  namespace = 'shared.order_templates'
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const useLiquidRendererTyped = useLiquidRenderer as unknown as (args: { sampleData?: Record<string, unknown> }) => {
    render: (content: string, sampleData: Record<string, unknown> | undefined) => void;
    rendering: boolean;
    error: ReactNode;
    html: string;
  };
  const { render, rendering, error, html } = useLiquidRendererTyped({ sampleData });

  // Render template when modal opens or content changes
  useEffect(() => {
    if (visible && content) {
      render(content, sampleData);
    }
  }, [visible, content, sampleData, render]);

  return (
    <Modal
      title={t(`${namespace}.form.preview_title`)}
      open={visible}
      onCancel={onCancel}
      width="90%"
      style={{ top: 20 }}
      footer={[
        <Button key="close" onClick={onCancel}>
          {t('common.button.close')}
        </Button>
      ]}
    >
      {rendering && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" tip={t(`${namespace}.detail.rendering`)} />
        </div>
      )}

      {error && (
        <Alert
          message={t(`${namespace}.detail.render_error`)}
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {!rendering && !error && html && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '20px',
            backgroundColor: '#f5f5f5',
            minHeight: '500px'
          }}
        >
          <TemplateIframe
            ref={iframeRef}
            html={html}
            paperSize={paperSize}
            cssVariablePrefix={cssVariablePrefix}
          />
        </div>
      )}
    </Modal>
  );
};

export default TemplatePreviewModal;
